import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TreeDeciduous, Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await api.get(`/api/auth/password-reset/validate/${token}`);
        setTokenValid(response.data.valid);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password || !formData.confirmPassword) {
      toast.error(t('resetPw.felderAusfuellen'));
      return;
    }

    if (formData.password.length < 8) {
      toast.error(t('resetPw.passwortZuKurz'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('resetPw.passwortUngleich'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/auth/password-reset/confirm', {
        token,
        new_password: formData.password
      });
      setSuccess(true);
      toast.success(t('resetPw.erfolg'));
    } catch (error) {
      console.error('Reset error:', error);
      const message = error.response?.data?.detail || t('resetPw.aendernFehler');
      toast.error(message);
      if (message.includes('abgelaufen') || message.includes('Ungültiger') || message.includes('Sitzung')) {
        setTokenValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500">{t('resetPw.wirdGeprueft')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="p-2 rounded-lg bg-primary">
            <TreeDeciduous className="h-6 w-6 text-white" />
          </div>
          <span className="font-serif text-2xl font-bold text-primary">ForestHub</span>
        </Link>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {!tokenValid ? 'Link ungültig' : success ? 'Passwort geändert!' : 'Neues Passwort'}
            </CardTitle>
            <p className="text-gray-500">
              {!tokenValid 
                ? t('resetPw.linkAbgelaufen')
                : success 
                  ? t('resetPw.jetztAnmelden')
                  : t('resetPw.sicheresPasswort')}
            </p>
          </CardHeader>
          
          <CardContent>
            {!tokenValid ? (
              // Invalid token state
              <div className="text-center space-y-4" data-testid="reset-password-invalid">
                <div className="flex justify-center">
                  <div className="p-3 rounded-full bg-red-100">
                    <XCircle className="h-8 w-8 text-red-600" />
                  </div>
                </div>
                <p className="text-gray-600">
                  {t('resetPw.neuenLink')}
                </p>
                <div className="flex flex-col gap-2">
                  <Button asChild className="bg-primary hover:bg-primary-600">
                    <Link to="/passwort-vergessen">
                      {t('resetPw.neuenLinkBtn')}
                    </Link>
                  </Button>
                  <Button asChild variant="ghost">
                    <Link to="/anmelden">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {t('resetPw.zurAnmeldung')}
                    </Link>
                  </Button>
                </div>
              </div>
            ) : success ? (
              // Success state
              <div className="text-center space-y-4" data-testid="reset-password-success">
                <div className="flex justify-center">
                  <div className="p-3 rounded-full bg-green-100">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <p className="text-gray-600">
                  {t('resetPw.erfolgText')}
                </p>
                <Button 
                  onClick={() => navigate('/anmelden')} 
                  className="bg-primary hover:bg-primary-600"
                >
                  {t('resetPw.jetztAnmeldenBtn')}
                </Button>
              </div>
            ) : (
              // Reset form
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('resetPw.neuesPasswort')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={t('resetPw.mindestens8')}
                      className="pl-10 pr-10"
                      data-testid="reset-password-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('resetPw.bestaetigen')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder={t('resetPw.wiederholen')}
                      className="pl-10"
                      data-testid="reset-password-confirm-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-600"
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? t('common.loading') : t('resetPw.aendernBtn')}
                </Button>

                <Button asChild variant="ghost" className="w-full">
                  <Link to="/anmelden">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('resetPw.zurAnmeldung')}
                  </Link>
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
